import React, { ComponentType } from 'react'
import { matchPath, Redirect, Route, RouteProps, Switch, useLocation } from 'react-router-dom'

// checkout
import Checkout from './views/Checkout/Checkout.view'
import { checkoutRoute } from './views/Checkout/route'

// guidelines
import Guidelines from './views/Guidelines/Guidelines.view'
import { guidelinesRoute } from './views/Guidelines/route'

// lot
import Lot from './views/Lot/Lot.view'
import { lotBaseRoute, lotRoute, lotRouteNotFound } from './views/Lot/route'

// app frame
import AppFrame from './views/AppFrame/AppFrame'

// payment success
import { paymentSuccessRoute } from './views/PaymentSuccess/route'
import PaymentSuccess from './views/PaymentSuccess/PaymentSuccess.view'

// home
import Home from './views/Home/Home.view'
import { homeRoute } from './views/Home/route.const'

// search
import Search from './views/Search/Search.view'
import { searchRoute } from './views/Search/route'

// 404
import { notFoundRoute } from './views/404/route.const'
import NotFound from '@views/404/NotFound.component'

// profile
import { profileRoute } from './views/Profile/route.const'
import Profile from './views/Profile/Profile.view'

// login
import { loginRoute } from './views/Login/route.const'
import Login from './views/Login/Login.view'

// register
import { registerRoute } from './views/Register/route.const'
import Register from './views/Register/Register.view'

// MyVehicle
import { myVehicleRoute } from './views/MyVehicle/route.const'
import MyVehicle from './views/MyVehicle/MyVehicle.view'

// PersonalInfo
import { personalInfoRoute } from './views/PersonalInfo/route.const'
import PersonalInfo from './views/PersonalInfo/PersonalInfo.view'

// booking details
import BookingDetails from './views/BookingDetails/BookingDetails.view'
import { bookingDetailsRoute } from './views/BookingDetails/route.const'

// subscription details
import SubscriptionDetails from './views/SubscriptionDetails/SubscriptionDetails.view'
import { subscriptionDetailsRoute } from './views/SubscriptionDetails/route.const'

// carpay home
import carpayHomeRoute from '@views/Carpay/Home/route.const'
import CarpayHome from '@views/Carpay/Home/Home.view'

// faq - frequently asked questions
import Help from './views/Help/Help.view'
import { helpRoute } from './views/Help/route.const'
import PrivateRoute from './components/PrivateRoute/PrivateRoute'
import UnauthenticatedRoute from './components/UnauthenticatedRoute/UnauthenticatedRoute'
import AccountPayment from './views/AccountPayment/AccountPayment.view'
import { accountPaymentRoute } from './views/AccountPayment/route.const'
import carpayOrderListRoute from '@views/Carpay/OrderList/route.const'
import CarpayOrderList from '@views/Carpay/OrderList/OrderList.view'
import { useGetUser } from '@hooks/useAuth'
import FullScreenLoader from '@components/FullScreenLoader/FullScreenLoader.component'
import { useAuth } from '@context/AuthContext/AuthContext'
import { carpayOrderRoute } from '@views/Carpay/OrderInfo/route.const'
import CarpayOrderInfo from '@views/Carpay/OrderInfo/OrderInfo.view'
import { carpayPaymentInputRoute } from '@views/Carpay/PaymentInput/route.const'
import CarpayPaymentInput from '@views/Carpay/PaymentInput/PaymentInput.view'
import { carpayPaymentEmailRoute } from '@views/Carpay/PaymentEmail/route.const'
import CarpayPaymentEmail from '@views/Carpay/PaymentEmail/PaymentEmail.view'
import { carpayPaymentMethodRoute } from '@views/Carpay/PaymentMethod/route.const'
import CarpayPaymentMethod from '@views/Carpay/PaymentMethod/PaymentMethod.view'
import CarpayNoFees from '@views/Carpay/NoFees/NoFees.view'
import carpayNoFeesRoute from '@views/Carpay/NoFees/route.const'
import carpayPayLaterRoute from '@views/Carpay/PayLater/route.const'
import CarpayPayLater from '@views/Carpay/PayLater/PayLater.view'
import carpayPaymentFinishedRoute from '@views/Carpay/PaymentFinished/route.const'
import CarpayPaymentFinished from '@views/Carpay/PaymentFinished/PaymentFinished.view'
import CarpayLPNotFound from '@views/Carpay/NotFound/NotFound/NotFound.view'
import carpayLPNotFoundRoute, {
    carpayLPNotFoundLocationSearchRoute,
    carpayLPNotFoundOverviewRoute,
    carpayLPNotFoundStayRoute
} from '@views/Carpay/NotFound/route.const'
import useCarpayBookingLotActivated from '@hooks/useCarpayBookingLotActivated'
import CarpayLocationSearch from '@views/Carpay/NotFound/LocationSearch/LocationSearch.view'
import CarpayLPNotFoundStay from '@views/Carpay/NotFound/Stay/Stay.view'
import CarpayLPNotFoundOverview from '@views/Carpay/NotFound/Overview/Overview.view'
import { carpayPaidOrderRoute } from '@views/Carpay/PaidOrder/route.const'
import PaidOrder from '@views/Carpay/PaidOrder/PaidOrder.view'
import LotNotFound from '@views/404/LotNotFound.component'
import { occupancyRoute } from '@views/Occupancy/route'
import Occupancy from '@views/Occupancy/Occupancy.view'
import { useTranslation } from 'react-i18next'
import { getLanguageBasedOnDomain } from '@utils/getDomainExtension'
import { SeoHead } from '@components/SeoHead/SeoHead.component'
import CityLandingPage from '@views/LandingPages/CityLandingPage.view'
import { allLandingPageRoutes } from '@views/LandingPages/routes'
import {
    Guest,
    guestFinalRoute,
    GuestFinal,
    GuestRegistration,
    guestRegistrationRoute,
    guestRoute,
    GuestTimeDiscount,
    guestTimeDiscountRoute
} from '@views/Guest'

interface RouteType extends RouteProps {
    config: { hasNavbar: boolean }
    key: string
    privateRoute?: boolean
    unauthenticatedRoute?: boolean
    redirect?: string
    component?: ComponentType
}

const commonRoutes: RouteType[] = [
    {
        key: 'home',
        path: homeRoute(),
        component: Home,
        exact: true,
        config: { hasNavbar: true }
    },
    {
        key: 'home-2',
        path: '/home',
        redirect: homeRoute(),
        exact: true,
        config: { hasNavbar: true }
    },
    {
        key: 'help',
        path: helpRoute(),
        component: Help,
        config: { hasNavbar: true }
    },
    {
        key: 'guidelines',
        path: guidelinesRoute(),
        component: Guidelines,
        exact: false,
        config: { hasNavbar: true }
    },
    {
        key: 'login',
        path: loginRoute(),
        exact: true,
        component: Login,
        unauthenticatedRoute: true,
        config: { hasNavbar: true }
    },
    {
        key: 'register',
        path: registerRoute(),
        component: Register,
        exact: true,
        unauthenticatedRoute: true,
        config: { hasNavbar: true }
    },
    {
        key: 'profile',
        path: profileRoute(),
        component: Profile,
        exact: true,
        privateRoute: true,
        config: { hasNavbar: true }
    },
    {
        key: 'personal-info',
        path: personalInfoRoute(),
        exact: true,
        component: PersonalInfo,
        privateRoute: true,
        config: { hasNavbar: true }
    },
    {
        key: 'account-payment',
        path: accountPaymentRoute(),
        component: AccountPayment,
        exact: true,
        privateRoute: true,
        config: { hasNavbar: true }
    },
    {
        key: 'my-vehicle',
        path: myVehicleRoute(),
        component: MyVehicle,
        exact: true,
        privateRoute: true,
        config: { hasNavbar: true }
    },
    {
        key: '404',
        exact: true,
        path: notFoundRoute(),
        component: NotFound,
        config: { hasNavbar: true }
    },
    {
        key: 'index',
        path: '/index.html',
        exact: true,
        redirect: '/',
        config: { hasNavbar: true }
    },
    {
        key: 'occupancy',
        path: occupancyRoute(),
        component: Occupancy,
        exact: true,
        config: { hasNavbar: true }
    },
    {
        key: 'not-found',
        path: '*',
        redirect: notFoundRoute(),
        config: { hasNavbar: true }
    }
]

const bookingRoutes: RouteType[] = [
    {
        key: 'checkout',
        path: checkoutRoute(),
        exact: true,
        component: Checkout,
        config: { hasNavbar: true }
    },
    {
        key: 'payment-success',
        path: paymentSuccessRoute(),
        exact: true,
        component: PaymentSuccess,
        config: { hasNavbar: true }
    },
    {
        key: 'lot',
        path: lotRoute(),
        component: Lot,
        config: { hasNavbar: true }
    },
    {
        key: 'lot-base',
        path: lotBaseRoute(),
        exact: true,
        component: Home,
        config: { hasNavbar: true }
    },
    {
        key: 'lot-temp-base',
        path: '/booking/search/lot/:lotId',
        exact: true,
        config: { hasNavbar: true }
    },
    {
        key: 'lot-not-found',
        path: lotRouteNotFound(),
        component: LotNotFound,
        exact: true,
        config: { hasNavbar: true }
    },
    {
        key: 'search',
        path: searchRoute(),
        exact: true,
        component: Search,
        config: { hasNavbar: true }
    },
    {
        key: 'booking-details',
        path: bookingDetailsRoute(),
        exact: true,
        component: BookingDetails,
        privateRoute: true,
        config: { hasNavbar: true }
    },
    {
        key: 'subscription-details',
        path: subscriptionDetailsRoute(),
        exact: true,
        component: SubscriptionDetails,
        privateRoute: true,
        config: { hasNavbar: true }
    }
]

const carpayRoutes: RouteType[] = [
    {
        key: 'carpay-home',
        path: carpayHomeRoute,
        exact: true,
        component: CarpayHome,
        config: { hasNavbar: true }
    },
    {
        key: 'carpay-pay-later',
        path: carpayPayLaterRoute,
        exact: true,
        component: CarpayPayLater,
        config: { hasNavbar: true }
    },
    {
        key: 'carpay-order-list',
        path: carpayOrderListRoute,
        exact: true,
        component: CarpayOrderList,
        config: { hasNavbar: false }
    },
    {
        key: 'carpay-lp-not-found',
        path: carpayLPNotFoundRoute,
        exact: true,
        component: CarpayLPNotFound,
        config: { hasNavbar: true }
    },
    {
        key: 'carpay-lp-not-found-location-search',
        path: carpayLPNotFoundLocationSearchRoute,
        exact: true,
        component: CarpayLocationSearch,
        config: { hasNavbar: true }
    },
    {
        key: 'carpay-lp-not-found-location-stay',
        path: carpayLPNotFoundStayRoute,
        exact: true,
        component: CarpayLPNotFoundStay,
        config: { hasNavbar: true }
    },
    {
        key: 'carpay-lp-not-found-location-overview',
        path: carpayLPNotFoundOverviewRoute,
        exact: true,
        component: CarpayLPNotFoundOverview,
        config: { hasNavbar: true }
    },
    {
        key: 'carpay-no-fees',
        path: carpayNoFeesRoute,
        exact: true,
        component: CarpayNoFees,
        config: { hasNavbar: true }
    },
    {
        key: 'carpay-paid-order', // View of CLOSED orders (paid/enforced/cancelled/etc)
        path: carpayPaidOrderRoute,
        exact: true,
        component: PaidOrder,
        privateRoute: true, // Only logged in users can access this route
        config: { hasNavbar: true }
    },
    {
        key: 'carpay-order-view', // View of orders to BE PAID (incomplete/unpaid)
        path: carpayOrderRoute(),
        exact: true,
        component: CarpayOrderInfo,
        config: { hasNavbar: false }
    },
    {
        key: 'carpay-payment-input',
        path: carpayPaymentInputRoute(),
        exact: true,
        component: CarpayPaymentInput,
        config: { hasNavbar: false }
    },
    {
        key: 'carpay-payment-email',
        path: carpayPaymentEmailRoute(),
        exact: true,
        component: CarpayPaymentEmail,
        config: { hasNavbar: false }
    },
    {
        key: 'carpay-payment-method',
        path: carpayPaymentMethodRoute(),
        exact: true,
        component: CarpayPaymentMethod,
        config: { hasNavbar: false }
    },
    {
        key: 'carpay-payment-finished',
        path: carpayPaymentFinishedRoute,
        exact: true,
        component: CarpayPaymentFinished,
        config: { hasNavbar: true }
    }
]

const landingPageRoutes = (): RouteType[] => {
    const landingPages: {
        route: {
            originalPath: string
            redirectPath: string
        }
        component: ComponentType
    }[] = allLandingPageRoutes().map((route) => {
        const { originalPath, redirectPath } = route
        const component = CityLandingPage
        return {
            route: {
                originalPath,
                redirectPath
            },
            component
        }
    })

    const routes: RouteType[] = []

    for (const landingPage of landingPages) {
        const { originalPath, redirectPath } = landingPage.route

        routes.push({
            key: `landing-page-${originalPath}`,
            path: originalPath,
            exact: true,
            component: landingPage.component,
            config: { hasNavbar: true }
        })

        routes.push({
            key: `redirect-${redirectPath}`,
            path: redirectPath,
            exact: true,
            redirect: originalPath,
            config: { hasNavbar: true }
        })
    }

    return routes
}

const guestRoutes: RouteType[] = [
    {
        key: 'guest',
        path: guestRoute(),
        exact: true,
        component: Guest,
        config: { hasNavbar: true }
    },
    {
        key: 'guest-time-discount',
        path: guestTimeDiscountRoute(),
        exact: true,
        component: GuestTimeDiscount,
        config: { hasNavbar: true }
    },
    {
        key: 'guest-registration',
        path: guestRegistrationRoute(),
        exact: true,
        component: GuestRegistration,
        config: { hasNavbar: true }
    },
    {
        key: 'guest-final',
        path: guestFinalRoute(),
        exact: true,
        component: GuestFinal,
        config: { hasNavbar: true }
    }
]

const routes: RouteType[] = [...bookingRoutes, ...carpayRoutes, ...landingPageRoutes(), ...guestRoutes, ...commonRoutes]

export const AppRoutes = () => {
    const { isLoggedIn, user } = useAuth()
    const { loading } = useGetUser()
    const { search, pathname } = useLocation()
    const queryParams = new URLSearchParams(search)
    const { i18n } = useTranslation()
    const seoLang = i18n.getFixedT(getLanguageBasedOnDomain())

    const isLotRoute = matchPath<{ lotId: string }>(pathname, { path: lotBaseRoute() })

    const lotId = isLotRoute?.isExact ? isLotRoute.params.lotId : queryParams.get('lot_id')
    const [loadingLots] = useCarpayBookingLotActivated(lotId?.toUpperCase())

    // If logged in we show the spinner until we get the user data
    // If the user scanned a qr code, we wait until we get the lot data from both carpay and booking
    if (loading || (isLoggedIn && !user) || loadingLots) {
        return <FullScreenLoader displayDelay={200} />
    }

    return (
        <>
            <SeoHead title={seoLang('seo.defaultTitle')} description={seoLang('seo.defaultDescription')} />
            <Switch>
                {routes.map((route) => {
                    // temporary fix for redirection from booking
                    if (route.key === 'lot-temp-base') {
                        return (
                            <Route
                                key={route.key}
                                path={route.path}
                                exact={route.exact}
                                render={({ match }) => <Redirect to={lotBaseRoute(match.params.lotId)} />}
                            />
                        )
                    }
                    if (route.redirect) {
                        return (
                            <Route key={route.key} path={route.path} exact={route.exact}>
                                <Redirect to={route.redirect} />
                            </Route>
                        )
                    }

                    const Component = route.component
                    if (!Component) return

                    if (route.privateRoute) {
                        return (
                            <PrivateRoute key={route.key} path={route.path} exact={route.exact} strict={route.strict}>
                                <AppFrame config={route.config}>
                                    <Component />
                                </AppFrame>
                            </PrivateRoute>
                        )
                    }

                    if (route.unauthenticatedRoute) {
                        return (
                            <UnauthenticatedRoute
                                key={route.key}
                                path={route.path}
                                exact={route.exact}
                                strict={route.strict}
                            >
                                <AppFrame config={route.config}>
                                    <Component />
                                </AppFrame>
                            </UnauthenticatedRoute>
                        )
                    }

                    return (
                        <Route key={route.key} path={route.path} exact={route.exact} strict={route.strict}>
                            <AppFrame config={route.config}>
                                <Component />
                            </AppFrame>
                        </Route>
                    )
                })}
            </Switch>
        </>
    )
}
